/**
 * "Tutara en yakın ürün" şartı.
 *
 * SORUN
 * Müşteri satışın bir kısmını nakit, bir kısmını bankadan ödediğinde faturaya
 * yalnızca bankadan gelen tutar yazılır. Kayıttaki ürün aynı kalırsa birim fiyat
 * piyasanın altına düşer:
 *
 *   İşlem : 2 adet "1 Gram 24 Ayar" · 13.600 TL   (2.400 nakit + 11.200 havale)
 *   Fatura: 11.200 TL → 2 adet × 5.600 TL         (o gün gram altın ≈ 6.800 TL)
 *
 * ÇÖZÜM
 * Faturalanacak tutara Güncel Fiyat Ekranı'ndaki fiyatı en yakın ürünü seçeriz:
 *
 *   11.200 TL ≈ 1 adet Yeni Ziynet Çeyrek (≈ 11.085 TL)  → fark 115 TL
 *
 * Kural yalnızca birim fiyatın piyasa değerinin ALTINA düştüğü durumda çalışır;
 * tutarlar uyuşuyorsa hiçbir şey değişmez.
 */

import {
  mergeInvoiceProductCatalog,
  parseUnitGramsFromProductName
} from './invoiceProductCatalog';
import { lookupLiveProductPrice } from './invoiceLivePriceTable';

export const DEFAULT_AMOUNT_NEAREST_PRODUCT_RULE = {
  /** Şart açık mı */
  enabled: true,
  /** Ürün fiyatı ile fatura tutarı arasında kabul edilen en büyük fark (TL) */
  toleranceTl: 200,
  /** Tek faturada kaç adede kadar aynı üründen seçilebilir */
  maxPieces: 5,
  /** Aday ürünlerin alınacağı katalog kategorileri */
  categories: ['Sarrafiye', 'Gram Altın'],
  /**
   * ÜRÜN DEĞİŞTİRME için asgari banka payı (0-1).
   * Satışın küçük bir kısmı bankadan geldiyse (kapora gibi) başka bir ürüne
   * çevirmek yanlış olur; o durumda fatura eskisi gibi kesilir.
   * Aynı üründen adet azaltmada bu şart aranmaz.
   */
  minPaidRatio: 0.6,
  /** Ürün değiştirmeye izin verilen işlem kategorileri (takı/hurda hariç) */
  sourceCategories: ['Sarrafiye', 'Gram Altın']
};

export const mergeAmountNearestProductRule = (raw) => {
  const base = DEFAULT_AMOUNT_NEAREST_PRODUCT_RULE;
  if (raw == null) return { ...base };
  const tolerance = Number(raw.toleranceTl);
  const maxPieces = Number(raw.maxPieces);
  const categories = Array.isArray(raw.categories) && raw.categories.length ? raw.categories : base.categories;
  const sourceCategories =
    Array.isArray(raw.sourceCategories) && raw.sourceCategories.length
      ? raw.sourceCategories
      : base.sourceCategories;
  const minPaidRatio = Number(raw.minPaidRatio);
  return {
    enabled: raw.enabled !== false,
    toleranceTl: Number.isFinite(tolerance) && tolerance >= 0 ? tolerance : base.toleranceTl,
    maxPieces: Number.isFinite(maxPieces) && maxPieces >= 1 ? Math.min(Math.round(maxPieces), 20) : base.maxPieces,
    categories: categories.map((c) => String(c || '').trim()).filter(Boolean),
    minPaidRatio:
      Number.isFinite(minPaidRatio) && minPaidRatio >= 0 && minPaidRatio <= 1 ? minPaidRatio : base.minPaidRatio,
    sourceCategories: sourceCategories.map((c) => String(c || '').trim()).filter(Boolean)
  };
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Katalog satırının `pieces` adet için has (altın) değeri. Hesaplanamıyorsa 0. */
export const catalogRowHasValue = (row, pieces, hasFiyati) => {
  const has = Number(hasFiyati) || 0;
  const n = Number(pieces) || 0;
  if (!(has > 0) || !(n > 0) || !row) return 0;

  if (row.mode === 'hasFactor' && Number(row.hasFactor) > 0) {
    return round2(Number(row.hasFactor) * n * has);
  }
  if (row.mode === 'milyem' && Number(row.milyem) > 0) {
    const unitGrams = parseUnitGramsFromProductName(row.productName);
    if (!(unitGrams > 0)) return 0;
    return round2(unitGrams * n * has * Number(row.milyem));
  }
  return 0;
};

/**
 * Tutara en yakın katalog ürününü bulur.
 *
 * SIRALAMA: önce EN AZ ADET. Tolerans içinde 1 adetlik bir ürün varsa, 5 adetlik
 * daha yakın bir seçenek olsa bile 1 adetlik kazanır — "11.200 TL bir çeyreğe
 * denk geliyor" mantığı budur; beş tane 0,25 gramlık külçe değil.
 *
 * @returns {{row, pieces, unitPrice, totalPrice, diff, hasValue}|null}
 */
export const findNearestPricedProduct = ({
  amount,
  catalog,
  priceTable,
  rule = DEFAULT_AMOUNT_NEAREST_PRODUCT_RULE,
  hasFiyati = 0,
  priceScale = 1
}) => {
  const target = Number(amount) || 0;
  const cfg = mergeAmountNearestProductRule(rule);
  if (!(target > 0) || !cfg.enabled) return null;
  if (!priceTable || Object.keys(priceTable).length === 0) return null;

  const allowed = new Set(cfg.categories.map((c) => c.toLocaleLowerCase('tr-TR')));
  const rows = mergeInvoiceProductCatalog(catalog).filter(
    (r) => allowed.has(String(r.category || '').toLocaleLowerCase('tr-TR')) && (r.mode === 'hasFactor' || r.mode === 'milyem')
  );
  if (!rows.length) return null;

  // Fiyat tablosu "şu an"ı gösterir; işlem daha eski olabilir. Kayıttaki has
  // fiyatı ile bugünkü has fiyatının oranı kadar ölçekleyerek işlem anındaki
  // fiyata yaklaşırız.
  const scale = Number(priceScale);
  const safeScale = Number.isFinite(scale) && scale >= 0.5 && scale <= 2 ? scale : 1;

  const priced = rows
    .map((row) => ({
      row,
      unitPrice: round2(lookupLiveProductPrice(row.productName, priceTable) * safeScale)
    }))
    .filter((c) => c.unitPrice > 0);
  if (!priced.length) return null;

  for (let pieces = 1; pieces <= cfg.maxPieces; pieces += 1) {
    let best = null;
    priced.forEach(({ row, unitPrice }) => {
      const totalPrice = round2(unitPrice * pieces);
      const diff = round2(Math.abs(totalPrice - target));
      if (diff > cfg.toleranceTl) return;

      // Has bedeli faturalanacak tutarı aşarsa birim fiyat yine piyasanın
      // altında kalır; bu aday sorunu çözmüyor demektir.
      const hasValue = catalogRowHasValue(row, pieces, hasFiyati);
      if (hasValue > 0 && hasValue > target) return;

      if (!best || diff < best.diff) {
        best = { row, pieces, unitPrice, totalPrice, diff, hasValue };
      }
    });
    if (best) return best;
  }

  // Tek üründen tutturulamadı: iki FARKLI ürünün karışımını dene.
  // Örn. 60.000 TL → 1 tam (40.000) + 1 yarım (20.000).
  let bestCombo = null;
  for (let i = 0; i < priced.length; i += 1) {
    for (let j = i + 1; j < priced.length; j += 1) {
      for (let a = 1; a <= cfg.maxPieces; a += 1) {
        const aTotal = priced[i].unitPrice * a;
        if (aTotal - cfg.toleranceTl > target) break;
        for (let b = 1; b <= cfg.maxPieces; b += 1) {
          const toplam = round2(aTotal + priced[j].unitPrice * b);
          const diff = round2(Math.abs(toplam - target));
          if (toplam - cfg.toleranceTl > target) break;
          if (diff > cfg.toleranceTl) continue;

          const hasValue = round2(
            catalogRowHasValue(priced[i].row, a, hasFiyati) + catalogRowHasValue(priced[j].row, b, hasFiyati)
          );
          if (hasValue > 0 && hasValue > target) continue;

          const adet = a + b;
          if (!bestCombo || diff < bestCombo.diff || (diff === bestCombo.diff && adet < bestCombo.adet)) {
            bestCombo = {
              diff,
              adet,
              totalPrice: toplam,
              hasValue,
              combo: [
                { row: priced[i].row, pieces: a, unitPrice: priced[i].unitPrice, totalPrice: round2(aTotal) },
                { row: priced[j].row, pieces: b, unitPrice: priced[j].unitPrice, totalPrice: round2(priced[j].unitPrice * b) }
              ]
            };
          }
        }
      }
    }
  }
  if (bestCombo) return { ...bestCombo, row: bestCombo.combo[0].row, pieces: bestCombo.combo[0].pieces, unitPrice: bestCombo.combo[0].unitPrice };

  return null;
};
